import { useEffect, useRef, useState } from "react";
import { Spinner } from "./States";

// Native-app-style pull-to-refresh, MOBILE ONLY. When the active scroller is at
// the very top and the user drags down past a threshold, we reload the page —
// matching the gesture people expect from a phone app (and which the browser's
// own pull-to-refresh doesn't provide once the app is installed/standalone).
const THRESHOLD = 70; // px of (damped) pull needed to trigger a refresh
const MAX_PULL = 110; // visual cap on how far the indicator travels
const RESIST = 0.5; // drag damping so the pull feels weighty

export function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullRef = useRef(0);
  const startY = useRef<number | null>(null);
  const dragging = useRef(false);

  useEffect(() => {
    pullRef.current = pull;
  }, [pull]);

  useEffect(() => {
    // Mobile + touch only. Desktop keeps its normal behavior.
    if (!window.matchMedia("(max-width: 920px)").matches) return;
    if (!("ontouchstart" in window)) return;

    // We're "at the top" only if no scrolled inner pane is in the way and the
    // page itself isn't scrolled. Walk up from the touch target to the first
    // real scroller and check its scrollTop.
    const atTop = (target: EventTarget | null): boolean => {
      let el = target as HTMLElement | null;
      while (el && el !== document.body && el !== document.documentElement) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) {
          return el.scrollTop <= 0;
        }
        el = el.parentElement;
      }
      return (window.scrollY || document.documentElement.scrollTop || 0) <= 0;
    };

    const onStart = (e: TouchEvent) => {
      if (refreshing || e.touches.length !== 1) {
        startY.current = null;
        return;
      }
      startY.current = atTop(e.target) ? e.touches[0]!.clientY : null;
      dragging.current = false;
    };

    const onMove = (e: TouchEvent) => {
      if (startY.current == null || refreshing) return;
      const dy = e.touches[0]!.clientY - startY.current;
      if (dy <= 0) {
        if (dragging.current) {
          dragging.current = false;
          setPull(0);
        }
        return;
      }
      dragging.current = true;
      setPull(Math.min(MAX_PULL, dy * RESIST));
      // Stop the browser's native overscroll/rubber-band while we own the gesture.
      if (e.cancelable) e.preventDefault();
    };

    const onEnd = () => {
      if (startY.current == null) return;
      const trigger = dragging.current && pullRef.current >= THRESHOLD;
      startY.current = null;
      dragging.current = false;
      if (trigger) {
        setRefreshing(true);
        setPull(THRESHOLD);
        // Let the spinner paint, then reload.
        window.setTimeout(() => window.location.reload(), 400);
      } else {
        setPull(0);
      }
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [refreshing]);

  const ready = pull >= THRESHOLD;
  const visible = pull > 0 || refreshing;
  const travel = (refreshing ? THRESHOLD : pull) - 44;

  return (
    <div
      aria-hidden={!visible}
      style={{
        position: "fixed",
        top: "calc(env(safe-area-inset-top, 0px) + 4px)",
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 45,
        transform: `translateY(${travel}px)`,
        opacity: visible ? 1 : 0,
        transition: dragging.current ? "none" : "transform 0.2s ease, opacity 0.2s ease",
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-lg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--brand)",
          fontSize: "1.05rem",
        }}
      >
        {refreshing ? (
          <Spinner />
        ) : (
          <span
            style={{
              transform: `rotate(${ready ? 180 : Math.min(180, (pull / THRESHOLD) * 180)}deg)`,
              transition: dragging.current ? "none" : "transform 0.15s ease",
              lineHeight: 1,
            }}
          >
            ↓
          </span>
        )}
      </div>
    </div>
  );
}
